/**
 *  @class
 *  @function FreeShippingProgressBar
 *
 *  Cart tiers bar. Tier data comes from the JSON rendered by snippets/free-shipping.liquid.
 *  Re-computes message / steps / fill client-side (multi-currency rate) and keeps free
 *  products in sync with the tiers: added when a tier is reached, removed when the cart
 *  drops back under it. A gift the customer removes on purpose is not re-added during
 *  the session.
 */

if (!customElements.get('free-shipping-progress-bar')) {
  let currentPercent = 0; // Store the current percentage globally
  let giftSyncInFlight = false; // one gift operation at a time (the bar can be rendered twice on the cart page)
  const failedGifts = new Set(); // gifts that could not be added (sold out, ...) - do not retry this page load
  const STORAGE_PREFIX = 'cart-tier-gift:';
  const PLACEHOLDER = /\[amount\]|\[montant\]|\{\{\s*amount\s*\}\}/g;

  const storage = {
    get(key) { try { return sessionStorage.getItem(STORAGE_PREFIX + key); } catch (e) { return null; } },
    set(key, value) { try { sessionStorage.setItem(STORAGE_PREFIX + key, value); } catch (e) {} },
    remove(key) { try { sessionStorage.removeItem(STORAGE_PREFIX + key); } catch (e) {} },
  };

  class FreeShippingProgressBar extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      const data = this.querySelector('[data-tiers]');
      try {
        this.tiers = data ? JSON.parse(data.textContent) : [];
      } catch (e) {
        this.tiers = [];
      }
      this.cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      const rate = window.Shopify && Shopify.currency ? parseFloat(Shopify.currency.rate) : 1;
      this.rate = rate > 0 ? rate : 1;
      this.updateStatus();
      this.syncGifts();
    }

    amountOf(tier) {
      return Math.round(tier.amount * this.rate);
    }

    isReached(tier) {
      return this.cartTotal >= this.amountOf(tier);
    }

    positionOf(index) {
      const count = this.tiers.length;
      return count === 1 ? 100 : ((index + 1) / (count + 1)) * 100;
    }

    updateStatus() {
      if (!this.tiers.length) return;

      const message = this.querySelector('[data-tier-message]');
      const next = this.tiers.find((tier) => !this.isReached(tier));
      if (message) {
        if (next) {
          const remaining = this.amountOf(next) - this.cartTotal;
          const currencyFormat = window.theme.settings.money_with_currency_format || "${{amount}}";
          const money = `<strong>${formatMoney(remaining, currencyFormat)}</strong>`;
          message.innerHTML = (next.before || '').replace(PLACEHOLDER, money);
        } else {
          message.innerHTML = this.tiers[this.tiers.length - 1].reached || '';
        }
      }

      this.querySelectorAll('[data-tier-step]').forEach((step, index) => {
        step.classList.toggle('is-reached', this.isReached(this.tiers[index]));
      });

      let percent = 0;
      let prevAmount = 0;
      let prevPos = 0;
      for (let index = 0; index < this.tiers.length; index++) {
        const amount = this.amountOf(this.tiers[index]);
        const pos = this.positionOf(index);
        if (this.cartTotal >= amount) {
          percent = pos;
        } else {
          const span = Math.max(amount - prevAmount, 1);
          percent = prevPos + ((this.cartTotal - prevAmount) / span) * (pos - prevPos);
          break;
        }
        prevAmount = amount;
        prevPos = pos;
      }
      if (!next) percent = 100;

      this.animateProgressBar(Math.min(Math.max(percent, 0), 100) / 100);
    }

    animateProgressBar(progressPercentage) {
      const targetPercent = progressPercentage * 100;
      const startPercent = currentPercent;
      const duration = 200;
      const startTime = performance.now();

      const ease = (t) => t * (2 - t);

      const animate = (time) => {
        const elapsed = time - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = ease(progress);

        currentPercent = startPercent + (targetPercent - startPercent) * easedProgress;
        this.style.setProperty('--percent', `${currentPercent}%`);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          currentPercent = targetPercent;
          this.style.setProperty('--percent', `${currentPercent}%`);
        }
      };

      requestAnimationFrame(animate);
    }

    /* ---------- free products ---------- */

    async syncGifts() {
      if (giftSyncInFlight) return;
      for (const tier of this.tiers) {
        if (!tier.gift || !tier.gift.variant) continue;
        const inCart = Boolean(tier.lineKey);
        const addedKey = `${tier.key}:added`;
        const declinedKey = `${tier.key}:declined`;

        if (this.isReached(tier)) {
          if (inCart) {
            storage.set(addedKey, '1');
            continue;
          }
          if (storage.get(addedKey)) {
            // It was in the cart and the customer took it out: respect that choice.
            storage.set(declinedKey, '1');
            continue;
          }
          if (storage.get(declinedKey) || failedGifts.has(tier.key)) continue;

          giftSyncInFlight = true;
          const added = await this.addGift(tier);
          giftSyncInFlight = false;
          if (!added) {
            failedGifts.add(tier.key);
            continue;
          }
          storage.set(addedKey, '1');
          await this.refreshCart();
          return;
        }

        storage.remove(addedKey);
        storage.remove(declinedKey);
        if (inCart) {
          giftSyncInFlight = true;
          const removed = await this.removeGift(tier);
          giftSyncInFlight = false;
          if (removed) {
            await this.refreshCart();
            return;
          }
        }
      }
    }

    addGift(tier) {
      const properties = {};
      properties._gift = tier.key;
      return fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: tier.gift.variant, quantity: 1, properties }] }),
      })
        .then((response) => response.ok)
        .catch(() => false);
    }

    removeGift(tier) {
      return fetch(routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: tier.lineKey, quantity: 0 }),
      })
        .then((response) => response.ok)
        .catch(() => false);
    }

    // Re-render the cart drawer / cart page the same way cart.js does after a quantity change.
    async refreshCart() {
      const host = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
      if (!host || typeof host.getSectionsToRender !== 'function') {
        window.location.reload();
        return;
      }
      const sections = host.getSectionsToRender();
      const ids = [...new Set(sections.map((section) => section.section))].join(',');
      try {
        const response = await fetch(`${routes.cart_url}?sections=${ids}`);
        const html = await response.json();
        sections.forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container || !html[section.section]) return;
          const target = container.querySelector(section.selector) || container;
          const fresh = new DOMParser().parseFromString(html[section.section], 'text/html').querySelector(section.selector);
          if (fresh) target.innerHTML = fresh.innerHTML;
        });
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-tier-gift' });
        }
      } catch (e) {
        window.location.reload();
      }
    }
  }

  customElements.define('free-shipping-progress-bar', FreeShippingProgressBar);
}
